// The per-tick pipeline (DESIGN.md §7). The World is advanced in place and the
// events produced by this tick are returned.
import { computeDispatch } from './dispatch';
import { applyEconomy, rampFuelPrice } from './economy';
import type { SimEvent } from './events';
import { countHomes, updateHomes } from './homes';
import { spawnDaily } from './spawn';
import type { Balance, MapData, Season, World } from './types';

export function tick(world: World, map: MapData, balance: Balance): SimEvent[] {
  const events: SimEvent[] = [];
  if (world.status !== 'running') {
    return events;
  }

  // 1. Advance clock.
  const newDay = advanceClock(world, map, balance, events);

  // 2. Weather (slice 4).

  // 3–5. Demand, plant output, dispatch (derived).
  const dispatch = computeDispatch(world, map, balance);

  // 6. Home status.
  updateHomes(world, dispatch, balance, events);

  // 7. Economy.
  applyEconomy(world, dispatch, balance);
  const counts = countHomes(world);
  const ticksPerDay = balance.time.ticksPerHour * balance.time.hoursPerDay;
  world.score.homeDays += counts.fed / ticksPerDay;
  world.score.peakHomes = Math.max(world.score.peakHomes, counts.fed);

  if (newDay) {
    world.score.daysSurvived = world.time.day - 1;
    rampFuelPrice(world, map.economy.fuelGrowthPerDay ?? balance.economy.fuelGrowthPerDay);
    // 8. Spawn.
    spawnDaily(world, map, balance);
  }

  // 9. Game over.
  if (counts.total > 0 && counts.dark / counts.total >= balance.failure.darkHomeFraction) {
    world.status = 'over';
    events.push({ t: 'GameOver', day: world.time.day });
  }

  return events;
}

/** Returns true when this tick started a new day. */
function advanceClock(world: World, map: MapData, balance: Balance, events: SimEvent[]): boolean {
  const ticksPerDay = balance.time.ticksPerHour * balance.time.hoursPerDay;
  world.time.tick += 1;
  const tickOfDay = world.time.tick % ticksPerDay;
  world.time.hour = tickOfDay / balance.time.ticksPerHour;

  if (tickOfDay === 0) {
    world.time.day += 1;
    world.time.season = seasonForDay(world.time.day, map);
    events.push({ t: 'DayStarted', day: world.time.day });
    return true;
  }
  return false;
}

export function seasonForDay(day: number, map: MapData): Season {
  const order = map.seasons.order;
  const index = Math.floor((day - 1) / map.seasons.daysPerSeason) % order.length;
  return order[index] ?? 'spring';
}
